import { useDeviceContext } from "@jidlomat/terminal-core/contexts/DeviceContext";
import { useAppSelector } from "@jidlomat/terminal-core/hooks/utilsHooks/storeHooks";
import { selectInitialDataLoaded } from "@jidlomat/terminal-core/slices/appStateSlice";
import { selectAvailableDishTypesIds } from "@jidlomat/terminal-core/slices/dishesSlice";
import {
	selectFridgeState,
	selectHasFridgeError,
	selectHasNoActiveSalesPoint,
	selectTracksStock,
} from "@jidlomat/terminal-core/slices/fridgeInfoSlice";
import {
	selectHasActiveOrder,
	selectRefillMode,
	selectTransactionDishes,
} from "@jidlomat/terminal-core/slices/transactionSlice";
import { FridgeState, RefillTransactionMode } from "@jidlomat/terminal-core/types";
import type { TerminalFlow } from "@jidlomat/terminal-core/types/terminalFlow";
import { logToSentry } from "@jidlomat/terminal-core/utils/logger";
import { MealList } from "@jidlomat/terminal-menu/components/mealList/MealList";
import { SnackbarContextProvider } from "@jidlomat/terminal-ui/contexts/SnackbarContext";
import { DefaultLayout } from "@jidlomat/terminal-ui/layouts/DefaultLayout";
import { type ComponentType, lazy, Suspense } from "react";
import { ConnectionErrorModal } from "../components/global/modals/ConnectionErrorModal";
import { EmptyFridgeModal } from "../components/global/modals/EmptyFridgeModal";
import { NoActiveSalesPointModal } from "../components/global/modals/NoActiveSalesPointModal";
import { OutOfServiceModal } from "../components/global/modals/OutOfServiceModal";
import { RemoteRefillModal } from "../components/global/modals/RemoteRefillModal";
import { RestrictedModal } from "../components/global/modals/RestrictedModal";
import { StartingUpModal } from "../components/global/modals/StartingUpModal";
import { FlowErrorBoundary, MenuErrorBoundary } from "../recovery/ErrorBoundaries";

// The debug tools replay test fixtures and preview parts of the RFID flow. A production build never
// shows them and must not carry them, nor what they import: the condition is one the bundler can
// evaluate, and everywhere else they are a chunk of their own. The prototype playground (pnpm proto)
// shows the app as customers see it, without them.
const Debug =
	import.meta.env.MODE === "production" || import.meta.env.MODE === "proto"
		? null
		: lazy(
				(): Promise<{ default: ComponentType }> =>
					import("../components/global/DebugModal").then(
						(module) => ({ default: module.Debug }),
						(error: unknown) => {
							// A lazy component whose import was rejected throws into the render, and the boundary
							// around the screen of the flow would catch it: the error screen and a reload. The
							// page is worth more than its debug button, so it goes on without one.
							logToSentry("Debug tools failed to load", "warning", [error]);
							return { default: () => null };
						},
					),
			);

// The prototype playground's panel can ask the menu to crash, to show the menu boundary's fallback. Only a
// proto build mounts the trigger, as a chunk of its own, so no other build carries it.
const MenuCrashTrigger =
	import.meta.env.MODE === "proto"
		? lazy(() =>
				import("../components/global/MenuCrashTrigger").then(
					(module) => ({ default: module.MenuCrashTrigger }),
					(error: unknown) => {
						// As with the debug tools: the menu is worth more than its crash trigger.
						logToSentry("Menu crash trigger failed to load", "warning", [error]);
						return { default: () => null };
					},
				),
			)
		: null;

interface MealsContentProps {
	isLoading: boolean;
	hasDishes: boolean;
	showEmptyState: boolean;
	fridgeState: FridgeState | null;
	hasActiveOrder: boolean;
	refillMode: RefillTransactionMode | null;
	hasNoActiveSalesPoint: boolean;
	hasFridgeError: boolean;
	flow: TerminalFlow;
}

const MealsContent = ({
	isLoading,
	hasDishes,
	showEmptyState,
	fridgeState,
	hasActiveOrder,
	refillMode,
	hasNoActiveSalesPoint,
	hasFridgeError,
	flow,
}: MealsContentProps) => {
	const isRemoteRefill = refillMode === RefillTransactionMode.Remote;

	if (hasNoActiveSalesPoint) {
		return <NoActiveSalesPointModal />;
	}

	if (hasFridgeError) {
		// Any other fridge error falls back to the generic connection-error screen for now.
		// appConnected={false} mounts it unconditionally open.
		return <ConnectionErrorModal appConnected={false} />;
	}

	const statusScreen = getStatusScreen({
		isLoading: isLoading,
		hasDishes: hasDishes,
		showEmptyState: showEmptyState,
		fridgeState: fridgeState,
		hasActiveOrder: hasActiveOrder,
		isRemoteRefill: isRemoteRefill,
	});
	if (statusScreen) {
		return statusScreen;
	}

	return (
		<>
			<MenuErrorBoundary>
				<MealList tutorialContent={<flow.TutorialContent />} />
				{MenuCrashTrigger ? (
					<Suspense fallback={null}>
						<MenuCrashTrigger />
					</Suspense>
				) : null}
			</MenuErrorBoundary>
			<FlowErrorBoundary>
				<flow.Screen
					debugSlot={
						Debug && (
							<Suspense fallback={null}>
								<Debug />
							</Suspense>
						)
					}
				/>
			</FlowErrorBoundary>
		</>
	);
};

type MealsListPageProps = {
	/** The flow of this fridge; null until its chunk has arrived. */
	flow: TerminalFlow | null;
	/** The chunk could not be fetched. */
	flowFailed: boolean;
};

export const MealsListPage = ({ flow, flowFailed }: MealsListPageProps) => {
	const availableDishTypesIds = useAppSelector(selectAvailableDishTypesIds);
	const orderDishes = useAppSelector(selectTransactionDishes);
	const hasActiveOrder = useAppSelector(selectHasActiveOrder);
	const fridgeState = useAppSelector(selectFridgeState);
	const hasNoActiveSalesPoint = useAppSelector(selectHasNoActiveSalesPoint);
	const hasFridgeError = useAppSelector(selectHasFridgeError);
	const refillMode = useAppSelector(selectRefillMode);
	const { loading: isInitialQueryLoading } = useDeviceContext();
	const initialDataLoaded = useAppSelector(selectInitialDataLoaded);
	const tracksStock = useAppSelector(selectTracksStock);

	const hasDishes = availableDishTypesIds.size > 0 || orderDishes.length > 0;
	/*
	We delay the display of an empty state because it can cause false positives as the initialQuery
	can be loaded, but the available dishes can be empty before it gets data from the query.
	A fridge without readers cannot know that it is empty, so it never shows the empty state.
	 */
	const showEmptyState = initialDataLoaded && !hasDishes && tracksStock;

	if (flow === null) {
		// Nothing of the page can run without the flow. Until the initial state is in, this is the
		// starting-up screen the page always had, reported as such; once it is in, the screen only
		// bridges the fetch of the chunk and is not reported.
		return (
			<DefaultLayout>
				<SnackbarContextProvider>
					{flowFailed ? (
						<ConnectionErrorModal appConnected={false} />
					) : (
						<StartingUpModal tracked={!initialDataLoaded} />
					)}
				</SnackbarContextProvider>
			</DefaultLayout>
		);
	}

	return (
		<DefaultLayout>
			<SnackbarContextProvider>
				<flow.Provider>
					<MealsContent
						isLoading={isInitialQueryLoading}
						hasDishes={hasDishes}
						showEmptyState={showEmptyState}
						fridgeState={fridgeState}
						hasActiveOrder={hasActiveOrder}
						refillMode={refillMode}
						hasNoActiveSalesPoint={hasNoActiveSalesPoint}
						hasFridgeError={hasFridgeError}
						flow={flow}
					/>
				</flow.Provider>
			</SnackbarContextProvider>
		</DefaultLayout>
	);
};

type StatusScreenInput = Pick<
	MealsContentProps,
	"isLoading" | "hasDishes" | "showEmptyState" | "fridgeState" | "hasActiveOrder"
> & { isRemoteRefill: boolean };

// The screen that takes the place of the menu in MealsContent for the fridge's state, its stock or a remote refill:
// the first that applies, in this order, after the sales point and a fridge error. Null when the menu shows.
const getStatusScreen = (status: StatusScreenInput) => {
	const { isLoading, hasDishes, showEmptyState, fridgeState, hasActiveOrder, isRemoteRefill } = status;

	if (fridgeState === FridgeState.OutOfService) {
		return <OutOfServiceModal />;
	}

	if (fridgeState === FridgeState.Restricted && !hasActiveOrder) {
		if (isRemoteRefill) {
			return <RemoteRefillModal />;
		}
		return <RestrictedModal />;
	}

	const isStartingUp = isLoading && !hasDishes && !(fridgeState === FridgeState.Restricted && hasActiveOrder);
	const isEmpty =
		!(isLoading || hasDishes) && showEmptyState && !(fridgeState === FridgeState.Restricted && hasActiveOrder);

	if (isStartingUp) {
		return <StartingUpModal />;
	}

	if (isEmpty && !isRemoteRefill) {
		return <EmptyFridgeModal />;
	}

	if (isRemoteRefill) {
		return <RemoteRefillModal />;
	}

	return null;
};
